/**
 * IA-R13 / R13b — Capability Registry Soft façade + Soft Soft product lanes.
 * Logical capability ids are stable; adapters swap on the server only.
 * Soft Soft honesty: never paint Live Deep / MQTT binder as armed.
 */
import { escapeHtml } from "./dom.js";

/** @type {ReadonlyArray<{ id: string, label: string, honesty: string }>} */
export const CAPABILITY_REGISTRY = Object.freeze([
  { id: "summary", label: "Summary", honesty: "Preview or live, from the Watch" },
  { id: "risk", label: "Risk score", honesty: "Live · rules own the score" },
  { id: "why", label: "Why line", honesty: "A short why when a plan waits" },
  { id: "vision", label: "Scene text", honesty: "Preview · boxes not live" },
  { id: "intent", label: "Guess (shadow)", honesty: "Preview · never binds a live channel" },
  { id: "prediction", label: "Prediction", honesty: "Preview · no theater fulfill" },
  { id: "deep", label: "Deep plan", honesty: "Preview process · live GPU not authorized" },
  { id: "agent", label: "Helper", honesty: "Shadow cycle · off the alarm path" },
  { id: "canary", label: "Canary", honesty: "Shadow only · never live-executes" },
  { id: "recommend", label: "Recommendation", honesty: "Preview or live actions" },
  { id: "decide_wait", label: "Decision wait", honesty: "Live gate · a person" },
]);

/**
 * Soft provider id for Governance — from settings.mode + last model_used.
 * @param {{ mode?: string } | null | undefined} settings
 * @param {string} [modelUsed]
 */
export function softActiveProviderLabel(settings, modelUsed = "") {
  const mode = String((settings && settings.mode) || "rules").trim().toLowerCase();
  const model = String(modelUsed || "").trim();
  if (model) return model.slice(0, 48);
  if (mode === "hybrid") return "Rules plus a short why";
  if (mode === "orchestrator") return "Preview planner";
  return "Rules";
}

/**
 * @param {object|null|undefined} lanesPack
 * @returns {string}
 */
export function softLanesMapChipLabel(lanesPack) {
  const pack = lanesPack && typeof lanesPack === "object" ? lanesPack : null;
  if (!pack || pack.soft !== true) return "Soft · loading";
  const chip = pack.map_chip && typeof pack.map_chip === "object" ? pack.map_chip : null;
  if (chip && chip.label) {
    const raw = String(chip.label);
    if (/mqtt|\/ai\/|#\/|:\d{4,5}/i.test(raw)) return "Preview · not live";
    return raw;
  }
  const lanes = pack.lanes && typeof pack.lanes === "object" ? pack.lanes : {};
  const ip = lanes.intent_prediction || {};
  const deep = lanes.deep_orchestrator || {};
  const vision = lanes.vision || {};
  return (
    `Preview · ${Number(ip.intents || 0)} guesses · ${Number(ip.predictions || 0)} forecasts`
    + ` · Deep ${String(deep.mode || "quiet")}`
    + ` · Scene ${vision.enabled ? "on" : "off"}`
  );
}

/**
 * Compact Soft Soft chip for map toolbar (does not cover the map canvas).
 * @param {object|null|undefined} lanesPack
 */
export function softLanesMapChipHtml(lanesPack) {
  const pack = lanesPack && typeof lanesPack === "object" ? lanesPack : null;
  const label = softLanesMapChipLabel(pack);
  const ready = !!(pack && pack.soft === true);
  const chip = pack && pack.map_chip && typeof pack.map_chip === "object" ? pack.map_chip : {};
  const missed = Number(chip.missed != null ? chip.missed : ((pack && pack.lanes && pack.lanes.intent_prediction && pack.lanes.intent_prediction.status && pack.lanes.intent_prediction.status.missed) || 0));
  const due = Number(chip.due != null ? chip.due : ((pack && pack.lanes && pack.lanes.intent_prediction && pack.lanes.intent_prediction.status && pack.lanes.intent_prediction.status.due) || 0));
  const meta = ready
    ? `${due > 0 ? `${due} due` : ""}${due > 0 && missed > 0 ? " · " : ""}${missed > 0 ? `${missed} missed` : (due <= 0 ? "shadow verify" : "")}`
    : "Loading preview…";
  return `
  <aside class="croc-soft-mapchip glass-panel glass-panel--micro" data-soft-mapchip="${ready ? "1" : "0"}" aria-label="Preview on map">
    <p class="croc-soft-mapchip__k">Preview</p>
    <p class="croc-soft-mapchip__v">${escapeHtml(label)}</p>
    <p class="muted croc-soft-mapchip__s">${escapeHtml(meta)} · not live</p>
  </aside>`;
}

/**
 * Soft Soft product lanes strip for Home (from GET /ai/soft-lanes).
 * Renders real counts — never paint that path, MQTT, or ports.
 * @param {object|null|undefined} lanesPack
 */
export function softLanesPerceiveHtml(lanesPack) {
  const pack = lanesPack && typeof lanesPack === "object" ? lanesPack : null;
  if (!pack || pack.soft !== true) {
    return `<aside class="croc-soft-lanes" data-soft-lanes="0" aria-label="Preview lanes">
      <p class="muted" style="margin:0;font-size:12px">Preview lanes loading…</p>
    </aside>`;
  }
  const lanes = pack.lanes && typeof pack.lanes === "object" ? pack.lanes : {};
  const ip = lanes.intent_prediction || {};
  const deep = lanes.deep_orchestrator || {};
  const vision = lanes.vision || {};
  const fast = lanes.fast_enrich || {};
  const agent = lanes.soft_agent || {};
  const canary = lanes.soft_canary || {};
  const lead = pack.lead && typeof pack.lead === "object" ? pack.lead : {};
  const status = ip.status && typeof ip.status === "object" ? ip.status : {};
  const tick = ip.tick && typeof ip.tick === "object" ? ip.tick : {};
  const honesty = Array.isArray(pack.honesty) ? pack.honesty : [];
  const intents = Array.isArray(lead.intents) ? lead.intents : [];
  const preds = Array.isArray(lead.predictions) ? lead.predictions : [];
  const leadId = String(lead.incident_id || "").trim();

  const intentLine = intents.length
    ? intents
        .slice(0, 2)
        .map((i) => `${escapeHtml(String(i.intent_code || "intent"))}${i.shadow ? " · shadow" : ""} · ${escapeHtml(String(i.status || ""))}`)
        .join(" · ")
    : `${Number(ip.shadow_intents != null ? ip.shadow_intents : ip.intents || 0)} shadow intents in tenant`;
  const predLine = preds.length
    ? preds
        .slice(0, 2)
        .map((p) => `${escapeHtml(String(p.observation_type || "pred"))} · ${escapeHtml(String(p.status || ""))}`)
        .join(" · ")
    : `${Number(ip.predictions || 0)} Soft predictions`;

  const statusBits = ["scheduled", "due", "missed", "fulfilled"]
    .map((k) => {
      const n = Number(status[k] || 0);
      return n > 0 ? `${n} ${k}` : "";
    })
    .filter(Boolean)
    .join(" · ");
  const tickBits = [
    Number(tick.due || 0) > 0 ? `tick due +${Number(tick.due)}` : "",
    Number(tick.missed || 0) > 0 ? `tick missed +${Number(tick.missed)}` : "",
  ].filter(Boolean).join(" · ");

  const deepMode = String(deep.mode || "empty");
  const deepLive = deep.live === true;
  const visionMode = vision.enabled ? "Soft on" : "off";
  const honestyLine = honesty.length
    ? honesty.slice(0, 4).map((h) => {
        const raw = String(h);
        if (/mqtt|\/ai\/|#\/|AI-DISP|localhost|:\d{4,5}/i.test(raw)) return "Preview is not live";
        return escapeHtml(raw);
      }).join(" · ")
    : "Notify first · rules keep the score · preview is not live";

  return `
  <aside class="croc-soft-lanes glass-panel glass-panel--inset" data-soft-lanes="1" data-soft-live-deep="${deepLive || pack.live_deep === true ? "1" : "0"}" aria-label="Preview lanes">
    <p class="croc-soft-lanes__eyebrow">Preview · not live</p>
    <div class="croc-soft-lanes__metrics" role="group" aria-label="Preview counts">
      <span class="croc-soft-lanes__metric"><strong>${Number(ip.intents || 0)}</strong> guesses</span>
      <span class="croc-soft-lanes__metric"><strong>${Number(ip.shadow_intents != null ? ip.shadow_intents : ip.intents || 0)}</strong> shadow</span>
      <span class="croc-soft-lanes__metric"><strong>${Number(ip.predictions || 0)}</strong> forecasts</span>
      <span class="croc-soft-lanes__metric">${escapeHtml(statusBits || "no check yet")}</span>
      ${tickBits ? `<span class="croc-soft-lanes__metric muted">${escapeHtml(tickBits)}</span>` : ""}
    </div>
    <ul class="croc-soft-lanes__list">
      <li><strong>Guess / forecast</strong>
        <span class="muted">${intentLine}</span>
        <span class="muted"> · ${predLine}</span>
        <span class="badge">shadow only</span>
      </li>
      <li><strong>Deep plan</strong>
        <span class="muted">${escapeHtml(deepMode)} · ${escapeHtml(String(deep.process || "quiet"))}${deep.ai_disp_d === "soft" ? " · preview" : ""}</span>
        <span class="badge">${deepLive ? "Live (unexpected)" : "Preview"}</span>
      </li>
      <li><strong>Scene</strong>
        <span class="muted">${escapeHtml(visionMode)} · boxes not live · score untouched</span>
        <span class="badge">${vision.enabled ? "On · preview" : "off"}</span>
      </li>
      <li><strong>Fast facts</strong>
        <span class="muted">${escapeHtml(String(fast.mode || "off"))}${fast.entry ? ` · ${escapeHtml(String(fast.entry))}` : ""}</span>
        <span class="badge">${fast.scheduler ? "on a timer" : "off"}</span>
      </li>
      <li><strong>Helper</strong>
        <span class="muted">${escapeHtml(String(agent.cycle || "open → tick → close"))} · ${agent.on_alarm_hot_path ? "on the alarm path (bad)" : "off the alarm path"}</span>
        <span class="badge">shadow only</span>
      </li>
      <li><strong>Canary</strong>
        <span class="muted">${escapeHtml(String(canary.mode || "shadow"))} · ${canary.live_executed === true ? "ran live (bad)" : "never ran live"}</span>
        <span class="badge">never live</span>
      </li>
    </ul>
    <p class="honest croc-soft-lanes__note">${honestyLine}</p>
  </aside>`;
}

/**
 * @param {{ settings?: object, modelUsed?: string, compact?: boolean, softLanes?: object|null }} [opts]
 */
export function capabilityRegistrySoftHtml(opts = {}) {
  const settings = opts.settings || {};
  const modelUsed = String(opts.modelUsed || "").trim();
  const provider = softActiveProviderLabel(settings, modelUsed);
  const compact = !!opts.compact;
  const rows = CAPABILITY_REGISTRY.map(
    (c) => `<li class="croc-capreg__item" data-cap-id="${escapeHtml(c.id)}">
      <strong>${escapeHtml(c.label)}</strong>
      <span class="muted">${escapeHtml(c.honesty)}</span>
    </li>`,
  ).join("");
  const softStrip = softLanesPerceiveHtml(opts.softLanes);
  return `
  ${softStrip}
  <aside class="croc-capreg${compact ? " croc-capreg--compact" : ""}" data-capability-registry="1" aria-label="Capability Registry">
    <p class="croc-capreg__eyebrow">What this Watch can do</p>
    <p class="croc-capreg__provider" title="Provider can swap without redesigning Home">
      On now: <strong>${escapeHtml(provider)}</strong>
    </p>
    ${compact
      ? `<p class="muted croc-capreg__hint">Summary · Risk · Why · Scene · Guess · Forecast · Deep plan · Helper · Canary · Decide — one list, the Watch stays the same.</p>`
      : `<ul class="croc-capreg__list">${rows}</ul>`}
    <p class="honest croc-capreg__note">This console never stores model keys. Preview is not live. Policy can swap without redesigning Home.</p>
  </aside>`;
}
